import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Plus,
  ExternalLink,
  Trash2,
  Copy,
  Check,
  Eye,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Globe,
  Layout,
  Sparkles,
  LayoutTemplate,
  Layers,
  Monitor,
  BarChart3,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore } from '@/client/stores/ui-store';
import { formatNumber, formatRelativeTime, copyToClipboard } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { OnboardingEmptyState, SearchEmptyState } from '@/client/components/ui/empty-state';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Input } from '@/client/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

// Type for page data from API
interface PageData {
  id: string;
  name: string;
  description?: string | null;
  slug: string;
  templateType: string;
  isPublished: boolean;
  totalViews: number;
  folderId: string;
  linkId?: string | null;
  publicUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Template type info for display
const templateInfo: Record<string, { label: string; color: string; icon: typeof Layout }> = {
  product: { label: 'Product', color: 'from-blue-500 to-cyan-500', icon: Layout },
  event: { label: 'Event', color: 'from-purple-500 to-pink-500', icon: Sparkles },
  portfolio: { label: 'Portfolio', color: 'from-amber-500 to-orange-500', icon: Layers },
  'link-in-bio': {
    label: 'Link in Bio',
    color: 'from-emerald-500 to-teal-500',
    icon: LayoutTemplate,
  },
};

// Decorative wave pattern for page preview background
function WavePattern({ color = '#000' }: { color?: string }) {
  return (
    <svg
      className="absolute bottom-0 left-0 right-0 h-12 opacity-10"
      viewBox="0 0 400 50"
      preserveAspectRatio="none"
    >
      <path d="M0,25 C100,50 200,0 300,25 C350,37 375,37 400,25 L400,50 L0,50 Z" fill={color} />
    </svg>
  );
}

// Browser frame mockup for page preview
function BrowserFrame({
  children,
  url,
  isPublished,
}: {
  children: React.ReactNode;
  url?: string;
  isPublished: boolean;
}) {
  return (
    <div className="rounded-xl overflow-hidden border-2 border-black/10 dark:border-white/10 bg-white dark:bg-zinc-900 shadow-lg">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-b from-zinc-100 to-zinc-50 dark:from-zinc-800 dark:to-zinc-850 border-b border-black/5 dark:border-white/5">
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5">
          <div className="size-2.5 rounded-full bg-red-400" />
          <div className="size-2.5 rounded-full bg-amber-400" />
          <div className="size-2.5 rounded-full bg-emerald-400" />
        </div>

        {/* URL bar */}
        <div className="flex-1 mx-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-zinc-800 rounded-md border border-black/5 dark:border-white/10">
            {isPublished ? (
              <Globe className="size-3 text-emerald-500" />
            ) : (
              <Monitor className="size-3 text-muted-foreground" />
            )}
            <span className="text-[10px] font-mono text-muted-foreground truncate">
              {url || 'Preview mode'}
            </span>
          </div>
        </div>
      </div>

      {/* Page content area */}
      <div className="relative aspect-[4/3] overflow-hidden">{children}</div>
    </div>
  );
}

// Page preview card component
function PageCard({
  page,
  onEdit,
  onDelete,
}: {
  page: PageData;
  onEdit: (page: PageData) => void;
  onDelete: (page: PageData) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!page.publicUrl) {
      toast.error('Page must be published to copy URL');
      return;
    }
    await copyToClipboard(page.publicUrl);
    setCopied(true);
    toast.success('URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const template = templateInfo[page.templateType] ?? templateInfo.product;
  const TemplateIcon = template?.icon ?? Layout;

  return (
    <Card className="group rounded-2xl overflow-hidden border-0 bg-gradient-to-br from-zinc-50 to-zinc-100/50 dark:from-zinc-900 dark:to-zinc-900/50 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/20 transition-all duration-500">
      <div className="p-4">
        {/* Browser frame with page preview */}
        <div className="relative">
          <BrowserFrame url={page.publicUrl || undefined} isPublished={page.isPublished}>
            {/* Stylized page preview */}
            <div
              className={`absolute inset-0 bg-gradient-to-br ${template?.color ?? 'from-blue-500 to-cyan-500'} opacity-10`}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              {/* Template icon */}
              <div
                className={`size-12 rounded-2xl bg-gradient-to-br ${template?.color ?? 'from-blue-500 to-cyan-500'} flex items-center justify-center mb-3 shadow-lg`}
              >
                <TemplateIcon className="size-6 text-white" />
              </div>
              {/* Page name preview */}
              <div className="w-3/4 h-3 bg-black/20 dark:bg-white/20 rounded-full mb-2" />
              <div className="w-1/2 h-2 bg-black/10 dark:bg-white/10 rounded-full" />

              <WavePattern />
            </div>

            {/* Hover overlay with actions */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="rounded-xl bg-white/90 hover:bg-white text-black"
                    onClick={() => onEdit(page)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit page</TooltipContent>
              </Tooltip>
              {page.isPublished && page.publicUrl && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="rounded-xl bg-white/90 hover:bg-white text-black"
                        onClick={handleCopy}
                      >
                        {copied ? (
                          <Check className="size-4 text-emerald-600" />
                        ) : (
                          <Copy className="size-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy URL</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a href={page.publicUrl} target="_blank" rel="noopener noreferrer">
                        <Button
                          size="icon"
                          variant="secondary"
                          className="rounded-xl bg-white/90 hover:bg-white text-black"
                        >
                          <ExternalLink className="size-4" />
                        </Button>
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>Open page</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </BrowserFrame>

          {/* Status badges - positioned on the frame */}
          <div className="absolute -top-1 -right-1 flex items-center gap-1">
            {page.isPublished ? (
              <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 shadow-lg shadow-emerald-500/20 text-[10px] px-2">
                <Globe className="size-2.5 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge
                variant="secondary"
                className="bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border-0 text-[10px] px-2"
              >
                Draft
              </Badge>
            )}
          </div>
        </div>

        {/* Card Info */}
        <div className="mt-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-base truncate">{page.name}</h3>
              {page.description && (
                <p className="text-sm text-muted-foreground line-clamp-1">{page.description}</p>
              )}
            </div>
            <Badge
              variant="outline"
              className={`shrink-0 text-[10px] border-0 bg-gradient-to-r ${template?.color ?? 'from-blue-500 to-cyan-500'} text-white`}
            >
              {template?.label ?? 'Page'}
            </Badge>
          </div>

          {/* Slug display */}
          <div className="flex items-center gap-2 text-xs">
            <code className="flex-1 px-2.5 py-1.5 bg-black/5 dark:bg-white/5 rounded-lg font-mono truncate text-muted-foreground">
              /p/{page.slug}
            </code>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-black/5 dark:border-white/5">
            <div className="text-center p-2 bg-black/5 dark:bg-white/5 rounded-lg">
              <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
                <Eye className="size-3" />
              </div>
              <p className="text-sm font-bold tabular-nums">{formatNumber(page.totalViews)}</p>
              <p className="text-[10px] text-muted-foreground">Views</p>
            </div>
            <Link
              to={`/analytics?asset=page&id=${page.id}`}
              className="text-center p-2 bg-black/5 dark:bg-white/5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer group/stat"
            >
              <div className="flex items-center justify-center gap-1 text-muted-foreground group-hover/stat:text-blue-600 mb-0.5">
                <BarChart3 className="size-3" />
              </div>
              <p className="text-sm font-bold tabular-nums group-hover/stat:text-blue-600">
                <TrendingUp className="size-3.5 inline" />
              </p>
              <p className="text-[10px] text-muted-foreground group-hover/stat:text-blue-600">
                Analytics
              </p>
            </Link>
          </div>

          {/* Footer with date and delete */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(page.createdAt)}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDelete(page)}
              className="opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Loading skeleton for page cards
function PageSkeleton() {
  return (
    <Card className="rounded-2xl overflow-hidden border-0 bg-gradient-to-br from-zinc-50 to-zinc-100/50 dark:from-zinc-900 dark:to-zinc-900/50">
      <div className="p-4">
        <div className="rounded-xl overflow-hidden border-2 border-black/5 dark:border-white/5">
          <div className="flex items-center gap-2 px-3 py-2 bg-zinc-100 dark:bg-zinc-800">
            <div className="flex items-center gap-1.5">
              <Skeleton className="size-2.5 rounded-full" />
              <Skeleton className="size-2.5 rounded-full" />
              <Skeleton className="size-2.5 rounded-full" />
            </div>
            <Skeleton className="flex-1 h-5 rounded-md" />
          </div>
          <Skeleton className="aspect-[4/3]" />
        </div>
        <div className="mt-4 space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-8 w-full rounded-lg" />
          <div className="flex justify-between pt-2 border-t">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-6 rounded" />
          </div>
        </div>
      </div>
    </Card>
  );
}

export function PagesPage() {
  const navigate = useNavigate();
  const { openDeleteConfirmation } = useUIStore();

  // URL-synced state
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString.withDefault(''));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));

  // Fetch pages
  const { data, isLoading, error } = useQuery({
    queryKey: ['landing-pages', { searchQuery, page }],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: '12',
        offset: ((page - 1) * 12).toString(),
      });
      // Note: Search is not implemented in API yet, but we prepare for it
      if (searchQuery) params.set('search', searchQuery);

      const response = await apiFetch(`/api/landing-pages?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch pages');
      }
      return response.json();
    },
    // Enable the query - API is ready
    enabled: true,
  });

  const handleDelete = (pageItem: PageData) => {
    openDeleteConfirmation('page', pageItem.id, pageItem.name);
  };

  const handleEdit = (pageItem: PageData) => {
    navigate(`/pages/${pageItem.id}/edit`);
  };

  // Calculate stats
  const totalViews = data?.data?.reduce((sum: number, p: PageData) => sum + p.totalViews, 0) ?? 0;
  const totalPages = data?.total ?? 0;
  const publishedCount = data?.data?.filter((p: PageData) => p.isPublished).length ?? 0;
  const totalPagesCount = Math.ceil(totalPages / 12);

  return (
    <div className="space-y-6 stagger-children relative">
      {/* Decorative background gradient */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-primary/20 via-accent/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-accent/20 via-primary/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 relative">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
              <FileText className="size-5 text-white" />
            </div>
            Pages
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Build beautiful landing pages
          </p>
        </div>
        <Button
          onClick={() => navigate('/pages/new')}
          size="lg"
          className="rounded-xl glow gap-2 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
        >
          <Plus className="size-5" />
          Create Page
        </Button>
      </div>

      {/* Quick Stats */}
      {totalPages > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <FileText className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Pages</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalPages)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Globe className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Published</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(publishedCount)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="size-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Eye className="size-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Views</p>
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalViews)}</p>
              </div>
            </CardContent>
          </Card>
          <Link to="/analytics?asset=page">
            <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 hover:shadow-lg hover:shadow-blue-500/10 transition-all cursor-pointer h-full">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="size-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <BarChart3 className="size-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                    View Analytics
                  </p>
                  <p className="text-xs text-blue-600/70 dark:text-blue-400/70">
                    See detailed insights
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search pages by name or slug…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="pl-12 h-12 rounded-xl border-black/10 dark:border-white/10"
        />
      </div>

      {/* Pages Grid */}
      {error ? (
        <Card className="rounded-2xl py-12 border-0 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/20 dark:to-rose-950/20">
          <CardContent className="text-center">
            <div className="size-24 rounded-3xl bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <FileText className="size-12 text-destructive/50" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-destructive">Error Loading Pages</h3>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <PageSkeleton key={i} />
          ))}
        </div>
      ) : data?.data?.length === 0 ? (
        searchQuery ? (
          <SearchEmptyState
            searchQuery={searchQuery}
            onClear={() => setSearchQuery('')}
            entityName="pages"
          />
        ) : (
          <OnboardingEmptyState
            icon={FileText}
            title="Create your first page"
            steps={[
              { icon: LayoutTemplate, text: 'Choose a beautiful template' },
              { icon: Pencil, text: 'Customize content and branding' },
              { icon: Globe, text: 'Publish and share with the world' },
            ]}
            action={{
              label: 'Create your first page',
              onClick: () => navigate('/pages/new'),
              icon: Plus,
            }}
          />
        )
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data?.data?.map((pageItem: PageData) => (
              <PageCard
                key={pageItem.id}
                page={pageItem}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPagesCount > 1 && (
            <div className="flex items-center justify-between py-4 border-t border-black/5 dark:border-white/5">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{(page - 1) * 12 + 1}</span>{' '}
                to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(page * 12, totalPages)}
                </span>{' '}
                of <span className="font-medium text-foreground">{totalPages}</span> pages
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeft className="size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPagesCount}
                  className="rounded-lg"
                >
                  Next
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
